import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import type { ColDef, GridApi, MenuItemDef } from 'ag-grid-community';
import {
  ColumnDefinitionInputV1,
  ColumnDefinitionOutputV1,
  RowRulesResultV1,
  RulesResultV1,
  TableDefinitionOutputV1,
} from '@/sdk';
import { FormulaErrorInterface } from '@/formula/formula.types';
import { SearchTypeEnum } from '@/sdk/model/PropertySearchV1';
import { BatchActionEnum } from '@/sdk/model/TableDefinitionInputV1';

export type MaterializedTableCell = string | number | boolean | undefined;
export type MaterializedTableRow = MaterializedTableCell[];

export interface MaterializedTableOutput {
  rows: MaterializedTableRow[];
  headers: MaterializedTableHeader[];
  hasMore: boolean;
  cursorKey?: string;
}

interface TableCell {
  override: boolean;
  evaluationDetails?: RulesResultV1[];
}

export interface ItemTableCell extends TableCell {
  uuid: string;
  fetchedProperties?: { [key: string]: string | undefined };
  tableDefinitionId?: string;
  isModifiedOutsideTable?: string | undefined;
}

export interface NumericTableCell extends TableCell {
  value?: number;
  uom?: string;
}

export interface TextTableCell extends TableCell {
  value?: string;
}

export interface BooleanTableCell extends TableCell {
  value?: boolean;
}

export type ProcessedTableCell = ItemTableCell | NumericTableCell | TextTableCell | BooleanTableCell;

export interface ProcessedMaterializedTable {
  rows: ProcessedTableCell[][];
  headers: MaterializedTableHeader[];
  hasMore: boolean;
}

export interface MaterializedTableHeader {
  name: string;
  type: ColumnTypeEnum;
}

export interface RowLoadProgress {
  progress: number;
  timingInformation?: string;
  meterInformation?: string;
}

export interface ItemTableColumnDefinition extends ColumnDefinitionInputV1 {
  /** Item Columns use this field for which Item Property that column is currently displaying */
  displayProperty?: string;
  /** Item columns use this field to keep track of all Properties that have been fetched, in conjunction with
   *  displayProperty, so we can cache the values for each property when we set the results of a Materialized
   *  Table in the store */
  additionalProperties?: string[];
  /** For most columns this will be the columnName, but some columns need a UI override for their name if they are
   * not editable by the user
   */
  displayName: string;
  evaluationDetails?: RowRulesResultV1[];
  isHiddenFromDropdown?: boolean;
}

export interface ScalingTableColumnDefinition extends ColumnDefinitionOutputV1 {
  /** Item Columns use this field for which Item Property that column is currently displaying */
  displayProperty?: string;
  /** Item columns use this field to keep track of all Properties that have been fetched, in conjunction with
   *  displayProperty, so we can cache the values for each property when we set the results of a Materialized
   *  Table in the store */
  additionalProperties?: string[];
  /** For most columns this will be the columnName, but some columns need a UI override for their name if they are
   * not editable by the user
   */
  displayName: string;
  evaluationDetails?: RowRulesResultV1[];
  isHiddenFromDropdown?: boolean;
}

export type TableColumnDefinition = ItemTableColumnDefinition | ScalingTableColumnDefinition;

export interface DecoratedScalingTableColumnDefinition extends ScalingTableColumnDefinition {
  dropdownDescription?: string;
  isAllowedForFirstInput?: boolean;
  isFormulaColumn?: boolean;
  isAssetColumn?: boolean;
  columnIsTextWithOnlyOneValue?: boolean;
  isMadeWithCreatorRule?: boolean;
}

export interface PotentialColumnDefinition extends ColumnDefinitionInputV1 {
  // The index of the column in the list of all columns, this is 1-based
  columnIndex: number;
  sourceColumnDisplayName: string;
  isOptionForDropdown: boolean;
  sourceColumnDisplayNameIfSourceIsNotYetCreated?: string;
  isAssetColumn?: boolean;
  isFormulaColumn?: boolean;
  dropdownDescription?: string;
  isAllowedForFirstInput?: boolean;
}

export interface MaterializedTableItemColumnPropertyPair {
  uuidColumn: string;
  propertyName: string;
}

export interface FormulaCompileResult {
  success: boolean;
  errors: FormulaErrorInterface[];
}

export const DEFAULT_TABLE_DEFINITION_NAME = 'New Table';

export const PROPERTY_COLUMN_MATCH_SEGMENT = '.properties.';
export const UOM_COLUMN_MATCH_SUFFIX = '_uom';

export interface AgGridScalingColumnDefinition extends ColDef {
  headerValueGetter: () => string;
  displayProperty?: string;
}

export interface TableDefinitionAccessSettings {
  /** Workbook ID or undefined to make table global */
  scopedTo?: string;
}

export interface HierarchyNode {
  itemId?: string | undefined;
  itemType?: string | undefined;
  name: string;
  parent?: HierarchyNode | undefined;
  children: Record<string, HierarchyNode[]>;
  path: string[];
  properties: Record<string, any>;
}

export interface HierarchySliceNode extends HierarchyNode {
  rootId?: string | undefined;
}

export interface EventData {
  data: HierarchyEventData;
}

export interface HierarchyEventData {
  hierarchySliceNode: HierarchySliceNode;
}

export interface TreeHierarchyNode {
  eventAccessor: string;
  type?: string | undefined;
  properties: Set<string>; // names
  children?: Map<string, TreeHierarchyNode>;
  count: number;
  uom?: string | undefined;
  example?: string | undefined;
}

export interface TreeHierarchy {
  hierarchy: Map<number, TreeHierarchyNode>; // hierarchy level
}

export const EDITOR_TYPES = {
  SCALING: 'SCALING',
  ITEM: 'ITEM',
} as const;
export type EditorType = typeof EDITOR_TYPES[keyof typeof EDITOR_TYPES];

export const TABLE_DEFINITION_COLUMN_OPTIONS = {
  ADD_COLUMN: { label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.ADD_COLUMN', value: 'ADD_COLUMN' },
  ADD_PROPERTY_TO_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.ADD_PROPERTY_TO_COLUMN',
    value: 'ADD_PROPERTY_TO_COLUMN',
  },
  EDIT_COLUMN: { label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.EDIT_COLUMN', value: 'EDIT_COLUMN' },
  TO_SIMPLE_TABLE: { label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.TO_SIMPLE_TABLE', value: 'TO_SIMPLE_TABLE' },
  CHANGE_DISPLAY_PROPERTY: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CHANGE_DISPLAY_PROPERTY',
    value: 'CHANGE_DISPLAY_PROPERTY',
  },
  CREATE_DESCENDANT_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CREATE_DESCENDANT_COLUMN',
    value: 'CREATE_DESCENDANT_COLUMN',
  },
  CREATE_ANCESTOR_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CREATE_ANCESTOR_COLUMN',
    value: 'CREATE_ANCESTOR_COLUMN',
  },
  CREATE_PROPERTY_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CREATE_PROPERTY_COLUMN',
    value: 'CREATE_PROPERTY_COLUMN',
  },
  CREATE_SHORTCUT_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CREATE_SHORTCUT_COLUMN',
    value: 'CREATE_SHORTCUT_COLUMN',
  },
};
export type TableDefinitionColumnOptions =
  typeof TABLE_DEFINITION_COLUMN_OPTIONS[keyof typeof TABLE_DEFINITION_COLUMN_OPTIONS];

// When interacting with the tableDefinitionStore, use the typed interface and define the editor type for each
// pass-through function.
export interface TableDefinitionActions {
  setSubscriptionId: (id: string | undefined) => void;
  setShouldSearchForItems: (shouldSearchForItems: boolean) => void;
  setName: (name: string) => void;
  setSearchTypeAI: (searchTypeAI: SearchTypeEnum | undefined) => void;
  setDescription: (description: string) => void;
  setBatchAction: (batchAction: BatchActionEnum) => void;
  setTableDefinition: (output: TableDefinitionOutputV1) => void;
  resetTableDefinition: () => void;
  setMaterializedTable: (materializedTable: MaterializedTableOutput | undefined) => void;
  setDoTableReload: (doTableReload: boolean) => void;
  setDisplayPropertyForColumn: (uuidColumnToPropertyNamePair: MaterializedTableItemColumnPropertyPair) => void;
  addPendingTableId: (pendingTableId: string) => void;
  removePendingTableId: (pendingTableId: string) => void;
  setEvaluationResults: (evaluationResults: { [key: string]: RowRulesResultV1[] }) => void;
  setShowEvaluationDetailModal: (show: boolean) => void;
  setEvaluationDetailsColumn: (
    column: ScalingTableColumnDefinition | undefined,
    evaluationResults: RulesResultV1[],
  ) => void;
  setCreatedItemFinderId: (id: string | undefined) => void;
  setAllowHiddenColumnsAsInputs: (allowHiddenColumnsAsInputs: boolean) => void;
  setAgGridApi: (agGridApi: GridApi) => void;
  setSelectedColumns: (selectedColumns: string[]) => void;
  setRowDataIsLoading: (rowDataIsLoading: boolean) => void;
  setRequestProgress: (payload: {
    id: string;
    progress: Record<string, unknown>;
    timingInformation?: Record<string, unknown>;
    meterInformation?: Record<string, unknown>;
  }) => void;
  setColumnOptions: (columnOptions?: TreeHierarchy | undefined) => void;
  setScrollToColumnId: (scrollToColumnId?: string) => void;
}

// When interacting with the tableEditorStore, use the typed interface and define the editor type for each
// pass-through function.
export interface TableDefinitionEditorActions {
  setTableId: (id: string) => void;
  setShowConfigureSearch: (show: boolean) => void;
  resetTableEditorStore: () => void;
  setColumnToEdit: (column: ScalingTableColumnDefinition | undefined) => void;
  setShowColumnProperties: (show: boolean) => void;
  setEnableTreeCreation: (enableTreeCreation: boolean) => void;
  setSelectedColumnOption: (selectedColumnOption?: TableDefinitionColumnOptions) => void;
  setShowSidePane: (show: boolean) => void;
  setShortcutSourceColumn: (shortcutSourceColumn?: ScalingTableColumnDefinition) => void;
}

// Used to allow Vantage to pass in noop functions for the required table definition actions
type ItemFinderTableDefinitionEditorActions = Pick<TableDefinitionEditorActions, 'setTableId'>;
type ItemFinderTableDefinitionActions = Pick<
  TableDefinitionActions,
  'addPendingTableId' | 'setTableDefinition' | 'setCreatedItemFinderId' | 'setDoTableReload'
>;
export type ItemFinderTableDefinitionCombinedActions = ItemFinderTableDefinitionActions &
  ItemFinderTableDefinitionEditorActions;

export interface MenuDefWithCount {
  menuDef: MenuItemDef;
  count: number;
}
